import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, TextField, Typography, Grid, FormControl,
  InputLabel, Select, MenuItem, IconButton, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Divider, InputAdornment
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import millingApi, {
  MillingOrder, MillingOrderInputRow, MillingOrderOutputRow, MillingOrderCostRow,
  MillingRecipe, OutputType, CostType
} from '../../services/millingApi';

const emptyInputRow: Partial<MillingOrderInputRow> = {
  lineNo: 1,
  itemId: '',
  itemCode: '',
  itemName: '',
  uom: 'KG',
  plannedQty: 0,
  actualQty: 0,
  warehouseId: '',
  warehouseName: '',
  unitCost: 0,
};

const emptyOutputRow: Partial<MillingOrderOutputRow> = {
  lineNo: 1,
  itemId: '',
  itemCode: '',
  itemName: '',
  uom: 'KG',
  outputType: 'main_product',
  isByProduct: false,
  isMainProduct: true,
  plannedQty: 0,
  actualQty: 0,
  warehouseId: '',
  warehouseName: '',
};

const emptyCostRow: Partial<MillingOrderCostRow> = {
  lineNo: 1,
  costType: 'labor',
  costDescription: '',
  plannedAmount: 0,
  actualAmount: 0,
};

const MillingOrderForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const recipeIdParam = searchParams.get('recipeId');
  const isEdit = Boolean(id) && id !== 'new';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recipes, setRecipes] = useState<MillingRecipe[]>([]);

  const [formData, setFormData] = useState<Partial<MillingOrder>>({
    orderNumber: '',
    orderDate: new Date().toISOString().split('T')[0],
    recipeId: '',
    recipeCode: '',
    recipeName: '',
    status: 'draft',
    plannedInputQty: 0,
    plannedOutputQty: 0,
    plannedYieldPercent: 0,
    notes: '',
    createdBy: 'admin',
  });

  const [inputRows, setInputRows] = useState<Partial<MillingOrderInputRow>[]>([]);
  const [outputRows, setOutputRows] = useState<Partial<MillingOrderOutputRow>[]>([]);
  const [costRows, setCostRows] = useState<Partial<MillingOrderCostRow>[]>([]);

  useEffect(() => {
    fetchRecipes();
    if (isEdit) {
      fetchOrder();
    } else if (recipeIdParam) {
      loadFromRecipe(recipeIdParam);
    }
  }, [id, recipeIdParam]);

  const fetchRecipes = async () => {
    try {
      const response = await millingApi.getRecipes({ status: 'active' });
      setRecipes(response.data || response);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const order = await millingApi.getOrder(id!);
      setFormData(order);
      setInputRows(order.inputRows || []);
      setOutputRows(order.outputRows || []);
      setCostRows(order.costRows || []);
    } catch (err) {
      console.error(err);
      setError('Error loading order');
    } finally {
      setLoading(false);
    }
  };

  const loadFromRecipe = async (recipeId: string) => {
    try {
      setLoading(true);
      const recipe = await millingApi.getRecipe(recipeId);

      // Map recipe to order
      setFormData((prev) => ({
        ...prev,
        recipeId: recipe.id,
        recipeCode: recipe.recipeCode,
        recipeName: recipe.recipeName,
        plannedInputQty: recipe.standardInputQty,
        plannedOutputQty: recipe.standardOutputQty,
        plannedYieldPercent: recipe.expectedYieldPercent,
      }));

      // Map recipe input rows to order input rows
      const mappedInputRows: Partial<MillingOrderInputRow>[] = (recipe.inputRows || []).map((r, idx) => ({
        lineNo: idx + 1,
        itemId: r.itemId,
        itemCode: r.itemCode,
        itemName: r.itemName,
        uom: r.uom,
        plannedQty: r.standardQty,
        actualQty: 0,
        warehouseId: r.warehouseId,
        warehouseName: r.warehouseName,
        unitCost: r.unitCost,
      }));
      setInputRows(mappedInputRows);

      // Map recipe output rows to order output rows
      const mappedOutputRows: Partial<MillingOrderOutputRow>[] = (recipe.outputRows || []).map((r, idx) => ({
        lineNo: idx + 1,
        itemId: r.itemId,
        itemCode: r.itemCode,
        itemName: r.itemName,
        uom: r.uom,
        outputType: r.outputType,
        isByProduct: r.isByProduct,
        isMainProduct: r.isMainProduct,
        plannedQty: r.expectedQty,
        actualQty: 0,
        warehouseId: r.warehouseId,
        warehouseName: r.warehouseName,
      }));
      setOutputRows(mappedOutputRows);

      // Create cost rows from recipe standard costs
      const defaultCostRows: Partial<MillingOrderCostRow>[] = [
        { lineNo: 1, costType: 'labor' as CostType, costDescription: 'Labor Cost', plannedAmount: recipe.standardLaborCost || 0, actualAmount: 0 },
        { lineNo: 2, costType: 'overhead' as CostType, costDescription: 'Overhead Cost', plannedAmount: recipe.standardOverheadCost || 0, actualAmount: 0 },
        { lineNo: 3, costType: 'depreciation' as CostType, costDescription: 'Depreciation', plannedAmount: recipe.standardDepreciationCost || 0, actualAmount: 0 },
        { lineNo: 4, costType: 'utilities' as CostType, costDescription: 'Utilities', plannedAmount: recipe.standardUtilitiesCost || 0, actualAmount: 0 },
      ].filter(c => c.plannedAmount && c.plannedAmount > 0);
      setCostRows(defaultCostRows);

    } catch (err) {
      console.error(err);
      setError('Error loading recipe');
    } finally {
      setLoading(false);
    }
  };

  const handleRecipeChange = (recipeId: string) => {
    if (recipeId) {
      loadFromRecipe(recipeId);
    }
  };

  const handleChange = (field: keyof MillingOrder, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleInputRowChange = (index: number, field: keyof MillingOrderInputRow, value: any) => {
    setInputRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleOutputRowChange = (index: number, field: keyof MillingOrderOutputRow, value: any) => {
    setOutputRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'outputType') {
        updated[index].isByProduct = value === 'by_product';
        updated[index].isMainProduct = value === 'main_product';
      }
      return updated;
    });
  };

  const handleCostRowChange = (index: number, field: keyof MillingOrderCostRow, value: any) => {
    setCostRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addInputRow = () => {
    setInputRows((prev) => [...prev, { ...emptyInputRow, lineNo: prev.length + 1 }]);
  };

  const removeInputRow = (index: number) => {
    setInputRows((prev) => prev.filter((_, i) => i !== index).map((row, i) => ({ ...row, lineNo: i + 1 })));
  };

  const addOutputRow = () => {
    setOutputRows((prev) => [...prev, { ...emptyOutputRow, lineNo: prev.length + 1, outputType: 'by_product', isByProduct: true, isMainProduct: false }]);
  };

  const removeOutputRow = (index: number) => {
    setOutputRows((prev) => prev.filter((_, i) => i !== index).map((row, i) => ({ ...row, lineNo: i + 1 })));
  };

  const addCostRow = () => {
    setCostRows((prev) => [...prev, { ...emptyCostRow, lineNo: prev.length + 1 }]);
  };

  const removeCostRow = (index: number) => {
    setCostRows((prev) => prev.filter((_, i) => i !== index).map((row, i) => ({ ...row, lineNo: i + 1 })));
  };

  const calculateTotals = () => {
    const plannedTotalCost = costRows.reduce((sum, r) => sum + (r.plannedAmount || 0), 0);
    return { plannedTotalCost };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.recipeId) {
      setError('Please select a recipe');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const { plannedTotalCost } = calculateTotals();

      const payload: any = {
        ...formData,
        inputRows: inputRows.map((row, idx) => ({ ...row, lineNo: idx + 1 })),
        outputRows: outputRows.map((row, idx) => ({ ...row, lineNo: idx + 1 })),
        costRows: costRows.map((row, idx) => ({ ...row, lineNo: idx + 1 })),
        plannedTotalCost,
      };

      if (isEdit) {
        await millingApi.updateOrder(id!, payload);
      } else {
        await millingApi.createOrder(payload);
      }
      navigate('/milling/orders');
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.message || 'Error saving order');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/milling/orders')} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4">{isEdit ? 'Edit Milling Order' : 'New Milling Order'}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        {/* Header Information */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Order Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Order Number"
                  value={formData.orderNumber}
                  onChange={(e) => handleChange('orderNumber', e.target.value)}
                  placeholder="Auto-generated if empty"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Order Date"
                  value={formData.orderDate}
                  onChange={(e) => handleChange('orderDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Recipe</InputLabel>
                  <Select
                    value={formData.recipeId || ''}
                    label="Recipe"
                    onChange={(e) => handleRecipeChange(e.target.value)}
                    disabled={isEdit}
                  >
                    {recipes.map((r) => (
                      <MenuItem key={r.id} value={r.id}>{r.recipeCode} - {r.recipeName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Planned Start Date"
                  value={formData.plannedStartDate || ''}
                  onChange={(e) => handleChange('plannedStartDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Planned End Date"
                  value={formData.plannedEndDate || ''}
                  onChange={(e) => handleChange('plannedEndDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Planned Input Qty"
                  value={formData.plannedInputQty}
                  onChange={(e) => handleChange('plannedInputQty', parseFloat(e.target.value) || 0)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Planned Output Qty"
                  value={formData.plannedOutputQty}
                  onChange={(e) => handleChange('plannedOutputQty', parseFloat(e.target.value) || 0)}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Input Materials */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Input Materials</Typography>
              <Button startIcon={<AddIcon />} onClick={addInputRow} size="small">Add Input</Button>
            </Box>
            {inputRows.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={50}>#</TableCell>
                      <TableCell>Item Code</TableCell>
                      <TableCell>Item Name</TableCell>
                      <TableCell>UOM</TableCell>
                      <TableCell align="right">Planned Qty</TableCell>
                      <TableCell align="right">Unit Cost</TableCell>
                      <TableCell align="right">Total Cost</TableCell>
                      <TableCell width={50}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inputRows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          <TextField size="small" value={row.itemCode} onChange={(e) => handleInputRowChange(idx, 'itemCode', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" value={row.itemName} onChange={(e) => handleInputRowChange(idx, 'itemName', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" value={row.uom} onChange={(e) => handleInputRowChange(idx, 'uom', e.target.value)} sx={{ width: 80 }} />
                        </TableCell>
                        <TableCell align="right">
                          <TextField size="small" type="number" value={row.plannedQty} onChange={(e) => handleInputRowChange(idx, 'plannedQty', parseFloat(e.target.value) || 0)} sx={{ width: 100 }} />
                        </TableCell>
                        <TableCell align="right">
                          <TextField size="small" type="number" value={row.unitCost} onChange={(e) => handleInputRowChange(idx, 'unitCost', parseFloat(e.target.value) || 0)} sx={{ width: 100 }} />
                        </TableCell>
                        <TableCell align="right">
                          ${((row.plannedQty || 0) * (row.unitCost || 0)).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() => removeInputRow(idx)}><DeleteIcon /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" align="center" sx={{ py: 2 }}>No input materials. Select a recipe or add manually.</Typography>
            )}
          </CardContent>
        </Card>

        {/* Output Products */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Output Products</Typography>
              <Button startIcon={<AddIcon />} onClick={addOutputRow} size="small">Add Output</Button>
            </Box>
            {outputRows.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={50}>#</TableCell>
                      <TableCell>Item Code</TableCell>
                      <TableCell>Item Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Planned Qty</TableCell>
                      <TableCell>Warehouse</TableCell>
                      <TableCell width={50}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {outputRows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          <TextField size="small" value={row.itemCode} onChange={(e) => handleOutputRowChange(idx, 'itemCode', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" value={row.itemName} onChange={(e) => handleOutputRowChange(idx, 'itemName', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <Select size="small" value={row.outputType} onChange={(e) => handleOutputRowChange(idx, 'outputType', e.target.value as OutputType)}>
                            <MenuItem value="main_product">Main Product</MenuItem>
                            <MenuItem value="by_product">By-Product</MenuItem>
                            <MenuItem value="waste">Waste</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell align="right">
                          <TextField size="small" type="number" value={row.plannedQty} onChange={(e) => handleOutputRowChange(idx, 'plannedQty', parseFloat(e.target.value) || 0)} sx={{ width: 100 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" value={row.warehouseName || ''} onChange={(e) => handleOutputRowChange(idx, 'warehouseName', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() => removeOutputRow(idx)}><DeleteIcon /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" align="center" sx={{ py: 2 }}>No output products. Select a recipe or add manually.</Typography>
            )}
          </CardContent>
        </Card>

        {/* Costs */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Planned Costs</Typography>
              <Button startIcon={<AddIcon />} onClick={addCostRow} size="small">Add Cost</Button>
            </Box>
            {costRows.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={50}>#</TableCell>
                      <TableCell>Cost Type</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Planned Amount</TableCell>
                      <TableCell width={50}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {costRows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          <Select size="small" value={row.costType} onChange={(e) => handleCostRowChange(idx, 'costType', e.target.value as CostType)}>
                            <MenuItem value="labor">Labor</MenuItem>
                            <MenuItem value="overhead">Overhead</MenuItem>
                            <MenuItem value="material">Material</MenuItem>
                            <MenuItem value="depreciation">Depreciation</MenuItem>
                            <MenuItem value="utilities">Utilities</MenuItem>
                            <MenuItem value="maintenance">Maintenance</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <TextField size="small" value={row.costDescription || ''} onChange={(e) => handleCostRowChange(idx, 'costDescription', e.target.value)} fullWidth />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            value={row.plannedAmount}
                            onChange={(e) => handleCostRowChange(idx, 'plannedAmount', parseFloat(e.target.value) || 0)}
                            sx={{ width: 120 }}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() => removeCostRow(idx)}><DeleteIcon /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} align="right"><Typography fontWeight="bold">Total Planned Cost:</Typography></TableCell>
                      <TableCell align="right"><Typography fontWeight="bold">${calculateTotals().plannedTotalCost.toFixed(2)}</Typography></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" align="center" sx={{ py: 2 }}>No costs defined.</Typography>
            )}
          </CardContent>
        </Card>

        {/* Notes */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Notes</Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={formData.notes || ''}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Add any notes or special instructions..."
            />
          </CardContent>
        </Card>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate('/milling/orders')}>Cancel</Button>
          <Button variant="contained" type="submit" disabled={saving}>
            {saving ? <CircularProgress size={24} /> : (isEdit ? 'Update Order' : 'Create Order')}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default MillingOrderForm;
